<template>
    <div class="head" :style="{ visibility: kiosk.isKioskMode ? 'hidden' : 'visible' }">
        <div class="title">{{ locationName }}</div>
        <UserMenuComponent />
    </div>
    <ParallaxHeader :logoPath="logoPath" :scrollableElement="scrollableElement" />
    <div
        class="scrollable"
        ref="scrollableElement"
        :class="{ hasparallax: hasParallax, 'has-kiosk-header': isKioskHeaderVisible }"
    >
        <div v-if="isKioskHeaderVisible" class="kiosk-header">
            <!-- the text is clamped to two rows, so the centering sits on the wrapper -->
            <span>{{ kioskHeader }}</span>
        </div>
        <ServicesGrid v-if="isServicesGridVisible" :services="services" />
    </div>
    <!-- outside of the head, so it can sit in the bottom right corner of the view -->
    <LanguageSelector v-if="kiosk.isKioskMode" />
</template>

<script setup lang="ts">
    const kiosk = useKioskStore();
    const language = useLanguageStore();
    import { TastierClient } from "@/app/TastierClient";
    import { ServicesControllerVue } from "@/app/controller/ServicesControllerVue";
    import type { IService } from "@/data/model/Service";
    import type { IMultiLanguageText } from "@/data/model/Shared";
    import { Format } from "@/data/util/Format";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { useLanguageStore } from "@/app/stores/language";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import ParallaxHeader from "@/app/components/header/ParallaxHeader.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import ServicesGrid from "@/app/components/service/ServicesGrid.vue";
    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";

    // header
    const isViewVisible = ref(false);
    const isMounted = ref(false);

    ServicesControllerVue.onShown.add(onBecomeVisible);
    ServicesControllerVue.onHidden.add(onBecomeInvisible);

    // state
    const locationNameFromAllocation = ref<string | undefined>(undefined);
    const locationInternalName = ref<string | undefined>(undefined);
    const isOperator = ref(false);
    const logoPath = ref<string | undefined>(undefined);
    const services = ref<IService[]>([]);
    const isServicesGridVisible = ref(false);
    const kioskHeaderText = ref<IMultiLanguageText | undefined>(undefined);

    // refs
    const scrollableElement = ref<HTMLElement>();

    // the grid is unmounted only after the view has faded out, so the tiles do not
    // disappear during the transition. the view can be shown again before that runs,
    // and then the pending timeout would blank an already visible view
    let hideGridTimeoutId: number | undefined;

    // computed
    const hasParallax = computed(() => logoPath.value !== undefined);
    // the Mobile POS prefers the internal name of the location
    const locationName = computed(() =>
        isOperator.value && (locationInternalName.value ?? "").trim().length > 0
            ? locationInternalName.value
            : locationNameFromAllocation.value
    );
    // read through the store language, so the header follows the kiosk language selector. it is
    // resolved in the exact language, so it is hidden instead of falling back to another one
    const kioskHeader = computed(() => Format.translate(kioskHeaderText.value, language.current) ?? "");
    const isKioskHeaderVisible = computed(() => kiosk.isKioskMode && kioskHeader.value.length > 0);

    // init components
    onMounted(() => {
        TastierClient.context.onAllocationChange.add(refreshGrid);
        // a login or logout while the view is shown. read from the identity context and not from the identity
        // store, which is updated by an event of its own and can still be behind when the allocation is reloaded
        TastierClient.identityContext.onIdentityChange.add(refreshState);
        isMounted.value = true;
        onBecomeVisible();
    });

    onBeforeUnmount(() => {
        TastierClient.identityContext.onIdentityChange.remove(refreshState);
        onBecomeInvisible();
        isMounted.value = false;
    });

    async function onBecomeVisible() {
        if (!isMounted.value) return;
        isViewVisible.value = true;

        window.clearTimeout(hideGridTimeoutId);
        hideGridTimeoutId = undefined;

        refreshState();
        isServicesGridVisible.value = true;
        await nextTick();

        await TastierClient.context.reloadBill();
    }

    async function onBecomeInvisible() {
        isViewVisible.value = false;
        window.clearTimeout(hideGridTimeoutId);
        hideGridTimeoutId = window.setTimeout(() => {
            hideGridTimeoutId = undefined;
            isServicesGridVisible.value = false;
        }, 500);
    }

    async function refreshState() {
        locationNameFromAllocation.value = TastierClient.context.currentAllocation?.locationName;
        locationInternalName.value = TastierClient.context.currentAllocation?.locationInternalName;
        isOperator.value = TastierClient.identityContext.isWaiterOrAdmin();
        logoPath.value = TastierClient.context.currentAllocation?.locationDesign.logoLightPath;
        services.value = TastierClient.context.currentAllocation?.services ?? [];
        kioskHeaderText.value = TastierClient.context.currentAllocation?.roomKioskHeader;
    }

    async function refreshGrid() {
        refreshState();

        // reset grid
        if (isServicesGridVisible.value) {
            isServicesGridVisible.value = false;
            await nextTick();
            isServicesGridVisible.value = true;
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .app > .services {
        > .scrollable {
            position: absolute;
            top: 53px;
            left: 0;
            right: 0;
            bottom: 0;
            font-size: 0;
            box-sizing: border-box;

            &.hasparallax {
                padding-top: 114px;
            }

            // the header carries the distance down to the first row of tiles, so the grid
            // follows it top aligned instead of centering in what is left of the height.
            // the view scrolls on its own once there are more services than fit
            &.has-kiosk-header {
                &.hasparallax {
                    padding-bottom: 20px;
                }

                > .kiosk-header {
                    padding: 3.5em 20px;
                    align-items: flex-start;
                }

                > .services-grid {
                    align-content: flex-start;
                    min-height: 0;
                    padding-bottom: 1em;
                }
            }

            > .kiosk-header {
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #fff;
                padding: 0 20px;

                // the anchor the em paddings above scale from. 1.75 times the kiosk
                // service label, which is clamp(26px, 3.3vw, 70px). it has to sit here
                // rather than on the span, because .scrollable sets font-size to 0
                font-size: 36px * variables.$kioskScale;

                > span {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    font-weight: 600;
                    color: colors.$fontColor;
                    text-align: center;
                }
            }
        }
    }
</style>
